import { useEffect, useRef, useState } from 'react';
import type { CorrelationByteResult } from '../../lib/decode/correlationEngine';

const COPY_FEEDBACK_MS = 1800;

type SimplifiedCandidatesPanelProps = {
  results: CorrelationByteResult[];
  byteFocusRequestId: number | null;
  onCreateSignalDraft: (result: CorrelationByteResult) => void;
  onExportJson: (result: CorrelationByteResult) => Promise<boolean> | boolean;
  onExportDbc: (result: CorrelationByteResult) => Promise<boolean> | boolean;
  onShowDetails: () => void;
};

type CopyKey = `${string}-${'json' | 'dbc'}`;

function getMatchLabel(confidence: CorrelationByteResult['confidence']) {
  switch (confidence) {
    case 'strong': return 'Strong match';
    case 'moderate': return 'Likely match';
    default: return 'Weak match';
  }
}

function rawConfidenceLabel(confidence: CorrelationByteResult['confidence']) {
  switch (confidence) {
    case 'strong': return 'High';
    case 'moderate': return 'Medium';
    default: return 'Low';
  }
}

function topMessage(top: CorrelationByteResult | undefined) {
  if (!top) return 'No clear candidate detected.';
  if (top.confidence === 'strong') return 'Strong candidate detected';
  if (top.confidence === 'moderate') return 'Likely candidate detected';
  return 'Weak candidate detected';
}

export function SimplifiedCandidatesPanel({
  results,
  onCreateSignalDraft,
  onExportJson,
  onExportDbc,
  onShowDetails
}: SimplifiedCandidatesPanelProps) {
  const top = results.slice(0, 2);
  const [draftedIds, setDraftedIds] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState<CopyKey | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimerRef = useRef<number | null>(null);
  const buttonTimerRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
    if (buttonTimerRef.current !== null) window.clearTimeout(buttonTimerRef.current);
  }, []);

  async function handleCreate(result: CorrelationByteResult) {
    setDraftedIds((current) => {
      const next = new Set(current);
      next.add(result.id);
      return next;
    });
    onCreateSignalDraft(result);
  }

  async function handleExport(result: CorrelationByteResult, kind: 'json' | 'dbc') {
    const handler = kind === 'json' ? onExportJson : onExportDbc;
    const ok = await handler(result);
    if (!ok) return;
    const key: CopyKey = `${result.id}-${kind}`;
    setCopied(key);
    setToastVisible(true);

    if (buttonTimerRef.current !== null) window.clearTimeout(buttonTimerRef.current);
    buttonTimerRef.current = window.setTimeout(() => {
      setCopied((current) => (current === key ? null : current));
      buttonTimerRef.current = null;
    }, COPY_FEEDBACK_MS);

    if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => {
      setToastVisible(false);
      toastTimerRef.current = null;
    }, COPY_FEEDBACK_MS);
  }

  if (top.length === 0) {
    return (
      <article className="simplified-candidates-panel" aria-live="polite">
        <div className="simplified-candidates-heading">
          <h3>No clear candidate detected</h3>
          <p>Try repeating the scenario, adjusting the event marker, or open advanced details.</p>
        </div>
        <button className="button ghost" type="button" onClick={onShowDetails}>Switch to Advanced Mode ▸</button>
      </article>
    );
  }

  return (
    <article className="simplified-candidates-panel" aria-live="polite">
      <div className="simplified-candidates-heading">
        <p className="eyebrow">{topMessage(top[0])}</p>
        <h3>Top {top.length === 1 ? 'candidate' : 'candidates'}</h3>
      </div>
      <ul className="simplified-candidate-list">
        {top.map((result) => {
          const drafted = draftedIds.has(result.id);
          return (
            <li className={`simplified-candidate-row strength-${result.confidence}`} key={result.id}>
              <div className="simplified-candidate-meta">
                <span className="simplified-candidate-canid">{result.canIdHex}</span>
                <span
                  className={`simplified-candidate-strength strength-${result.confidence}`}
                  title={`Confidence: ${rawConfidenceLabel(result.confidence)}`}
                >
                  {getMatchLabel(result.confidence)}
                </span>
                <span className="simplified-candidate-byte">Byte {result.byteIndex}</span>
              </div>
              {!drafted ? (
                <button
                  className="button primary"
                  type="button"
                  onClick={() => void handleCreate(result)}
                >
                  Create Signal
                </button>
              ) : (
                <div className="simplified-candidate-exports">
                  <span className="simplified-candidate-created">Signal created</span>
                  <button
                    className="button secondary small"
                    type="button"
                    onClick={() => void handleExport(result, 'json')}
                  >
                    {copied === `${result.id}-json` ? 'Copied ✓' : 'Export JSON'}
                  </button>
                  <button
                    className="button secondary small"
                    type="button"
                    onClick={() => void handleExport(result, 'dbc')}
                  >
                    {copied === `${result.id}-dbc` ? 'Copied ✓' : 'Export DBC'}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <div className="simplified-candidates-footer">
        <button className="button ghost" type="button" onClick={onShowDetails}>
          Switch to Advanced Mode ▸
        </button>
        <small>Show full engineering tools (graph, byte explorer, raw data).</small>
      </div>
      <div
        className={`simplified-toast${toastVisible ? ' is-visible' : ''}`}
        role="status"
        aria-live="polite"
      >
        Copied to clipboard
      </div>
    </article>
  );
}
